import { useState } from "react";
import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { Inbox, FolderKanban, Hammer, Type, LogOut, ExternalLink, Menu, X, KeyRound, FileText } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { useAuth, adminApi, formatApiError } from "../context/AuthContext";
import { useContentState } from "../context/ContentContext";

const navItems = [
  { to: "/admin/aanvragen", label: "Aanvragen", icon: Inbox, testid: "admin-nav-aanvragen" },
  { to: "/admin/projecten", label: "Projecten", icon: FolderKanban, testid: "admin-nav-projecten" },
  { to: "/admin/diensten", label: "Diensten", icon: Hammer, testid: "admin-nav-diensten" },
  { to: "/admin/teksten", label: "Teksten & instellingen", icon: Type, testid: "admin-nav-teksten" },
  { to: "/admin/voorwaarden", label: "Voorwaarden", icon: FileText, testid: "admin-nav-voorwaarden" },
  { to: "/admin/account", label: "Wachtwoord", icon: KeyRound, testid: "admin-nav-account" },
];

// Slaat een deel van de content op (bijv. { projects: [...] }) bovenop de huidige content.
export const useSaveContent = () => {
  const { content, reload } = useContentState();
  const [saving, setSaving] = useState(false);
  const save = async (patch) => {
    setSaving(true);
    try {
      const { updated_at, ...rest } = content;
      await adminApi.put("/admin/content", { ...rest, ...patch });
      await reload();
      toast.success("Opgeslagen", { description: "De wijzigingen staan direct op de site." });
      return true;
    } catch (err) {
      toast.error("Opslaan mislukt", { description: formatApiError(err) });
      return false;
    } finally {
      setSaving(false);
    }
  };
  return { save, saving };
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  if (user === null) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-[#8E8D8A]">Laden...</div>;
  }
  if (!user) return <Navigate to="/admin" replace state={{ from: location.pathname }} />;

  const nav = (
    <nav className="flex flex-col gap-1" aria-label="Beheer">
      {navItems.map(({ to, label, icon: Icon, testid }) => (
        <NavLink
          key={to}
          to={to}
          data-testid={testid}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-[4px] px-3 py-2.5 font-display text-sm font-bold uppercase tracking-wide transition-colors duration-200 ${
              isActive ? "bg-cream text-[#111111]" : "text-white/70 hover:bg-white/10 hover:text-white"
            }`
          }
        >
          <Icon className="h-4 w-4" /> {label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#F5F4F1] lg:grid lg:grid-cols-[260px_1fr]">
      <aside className="flex flex-col bg-[#111111] px-4 py-5 text-white lg:min-h-screen">
        <div className="flex items-center justify-between">
          <Logo light compact />
          <button type="button" className="lg:hidden" onClick={() => setOpen((v) => !v)} aria-label="Menu" data-testid="admin-menu-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        <div className={`${open ? "block" : "hidden"} mt-6 lg:block`}>
          {nav}
          <div className="mt-6 space-y-1 border-t border-white/10 pt-4">
            <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2 text-sm text-white/60 hover:text-white">
              <ExternalLink className="h-4 w-4" /> Bekijk de site
            </a>
            <button type="button" data-testid="admin-logout" onClick={logout} className="flex w-full items-center gap-3 px-3 py-2 text-sm text-white/60 hover:text-white">
              <LogOut className="h-4 w-4" /> Uitloggen
            </button>
          </div>
        </div>
        <div className="mt-auto hidden pt-8 lg:block">
          <p className="text-xs text-white/40">{user.email}</p>
          <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-cream/70">Gemaakt door @db_0226</p>
        </div>
      </aside>
      <main className="min-w-0 px-4 py-8 sm:px-8">
        <Outlet />
        <p className="mt-10 text-center text-[11px] uppercase tracking-[0.2em] text-[#B9B7B0] lg:hidden">Gemaakt door @db_0226</p>
      </main>
    </div>
  );
}
